import React, { useEffect, useRef, useState } from 'react';
import { Container } from '@components/atoms/Container';
import { Input, InputTypes, InputVariants, InputStates } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Form } from '@components/atoms/Form';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { useNavigate } from 'react-router-dom';
import { Typography } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { useTranslation } from 'react-i18next';
import { useGetDriverDocuments } from '@services/useGetDriverDocuments';
import { useDocumentsUpload } from '@services/useDocumentsUpload';
import { useNotifications } from '@hooks/useNotifications';
import styles from './DriverDocumentsDetailsArea.module.css';
import { DocumentUploadErrors, ExistingDocument } from '@types';
import { useData } from '@hooks/useData';

interface CustomFile extends File {
    blocked_for_edition?: boolean;
}

export const DriverDocumentsDetailsArea: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { language } = useData();
    const formRef = useRef<HTMLFormElement>(null);
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];
    const maxFileSize = 2.1 * 1024 * 1024;
    const { request: requestDriverDocuments } = useGetDriverDocuments();
    const {
        request: requestUpdateDocuments,
        error,
        validationErrors,
        lastResponseTime,
        success,
        documents,
    } = useDocumentsUpload();
    const { showDefaultNotification } = useNotifications();
    const [existingDocs, setExistingDocs] = useState<Record<string, ExistingDocument>>({});

    const translations = {
        title: t('documents-upload.title'),
        save: t('navigation.save'),
        CC: t('document-upload-step.card'),
        INS: t('document-upload-step.insurance'),
        DUA: t('document-upload-step.autoDoc'),
        selected: t('document-upload-step.selected'),
        fileError: t('document-upload-step.fileError'),
        fileRequired: t('document-upload-step.fileRequired'),
        fileTooLarge: t('document-upload-step.fileTooLarge'),
        atLeastOneFile: t('documents-upload.at-least-one'),
        invalidForm: t('documents-upload.invalid-form'),
    };

    const [files, setFiles] = useState<{ CC?: CustomFile; INS?: CustomFile; DUA?: CustomFile }>({
        CC: undefined,
        INS: undefined,
        DUA: undefined,
    });

    const [errors, setErrors] = useState<DocumentUploadErrors>({
        CC: [],
        INS: [],
        DUA: [],
    });

    /* get request */
    useEffect(() => {
        requestDriverDocuments().then((response) => {
            if (response?.data && Array.isArray(response?.data)) {
                const docs: Record<string, ExistingDocument> = {};
                response.data.forEach((doc) => {
                    docs[doc.document_type] = {
                        uuid: doc.uuid,
                        document_type: doc.document_type,
                        document_name: doc.document_name,
                        preview: doc.preview,
                        blocked_for_edition: doc.blocked_for_edition,
                    };
                });
                setExistingDocs(docs);
            }
        });
    }, []);

    const validateForm = (): boolean => {
        const hasErrors = Object.values(errors).some((arr) => arr && arr.length > 0);

        if (hasErrors) {
            showDefaultNotification({ type: 'error', message: translations.invalidForm });
            return false;
        }
        const filesArray = Object.values(files);
        const hasAtLeastOne = filesArray.some((file) => file !== undefined);

        if (!hasAtLeastOne) {
            showDefaultNotification({ type: 'error', message: translations.atLeastOneFile });
            return false;
        }

        return true;
    };

    const submitForm = () => {
        if (!validateForm()) return;
        const documentsToUpload = (['CC', 'INS', 'DUA'] as const)
            .map((key) => {
                const file = files[key];
                return file ? { type: key, file, uuid: existingDocs[key]?.uuid || null } : null;
            })
            .filter(Boolean) as { uuid: string; type: string; file: File }[];

        requestUpdateDocuments({
            documents: documentsToUpload,
            language: language,
        });
    };

    const resetForm = () => {
        setFiles({ CC: undefined, INS: undefined, DUA: undefined });
        if (formRef.current) {
            formRef.current.reset();
        }
    };

    useEffect(() => {
        if (lastResponseTime) {
            if (validationErrors) {
                setErrors({
                    CC: validationErrors['CC']?.flat() || [],
                    INS: validationErrors['INS']?.flat() || [],
                    DUA: validationErrors['DUA']?.flat() || [],
                });

                showDefaultNotification({ type: 'error', message: translations.invalidForm });
            } else if (error) {
                showDefaultNotification({ type: 'error', message: error });
            } else {
                showDefaultNotification({ type: 'success', message: success || '' });

                if (documents && Array.isArray(documents)) {
                    const docsMap: Record<string, ExistingDocument> = {};
                    documents.forEach((doc) => {
                        docsMap[doc.document_type] = {
                            uuid: doc.uuid,
                            document_type: doc.document_type,
                            document_name: doc.document_name,
                            preview: doc.preview,
                            blocked_for_edition: doc.blocked_for_edition,
                        };
                    });
                    setExistingDocs(docsMap);
                }
            }
            resetForm();
        }
    }, [lastResponseTime]);

    const handleFileChange = (key: string) => (fileList: FileList) => {
        const file = fileList?.[0] || null;

        setFiles((prev) => ({ ...prev, [key]: file || undefined }));

        setErrors((prev) => ({
            ...prev,
            [key]: file
                ? !allowedTypes.includes(file.type)
                    ? [translations.fileError]
                    : file.size > maxFileSize
                      ? [translations.fileTooLarge]
                      : ''
                : [translations.fileRequired],
        }));
    };
    const openPdf = (base64: string) => {
        if (!base64) return;

        const byteString = atob(base64.split(',')[1]);
        const arrayBuffer = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            arrayBuffer[i] = byteString.charCodeAt(i);
        }

        const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    };

    return (
        <Container classes={styles.containerBackground}>
            <div className={styles.header}>
                <RoundIcon
                    classes={styles.roundIcon}
                    icon="fa-thin fa-arrow-left"
                    variant={RoundIconVariants.variant1}
                    onClick={() => navigate(-1)}
                />

                <Typography fontSize="24" fontWeight="800" classes="m-auto">
                    {translations.title}
                </Typography>
            </div>
            <hr />

            <Form formRef={formRef} classes="d-flex flex-column gap-2" onEnterPress={submitForm} onSubmit={() => submitForm()}>
                <>
                    {(['CC', 'INS', 'DUA'] as const).map((key) => {
                        const hasError = errors[key].length > 0;
                        const isPdf = (existingDocs[key]?.preview || '').startsWith('data:application/pdf');
                        const previewUrl = existingDocs[key]?.preview || null;

                        return (
                            <div key={key}>
                                <Input
                                    disabled={existingDocs[key]?.blocked_for_edition || false}
                                    type={InputTypes.file}
                                    variant={InputVariants.labeledInput}
                                    label={`${translations[key]}*`}
                                    value={''}
                                    onChange={handleFileChange(key)}
                                    state={hasError ? InputStates.error : undefined}
                                    errorMessages={errors[key]}
                                />

                                {previewUrl &&
                                    !hasError &&
                                    (isPdf ? (
                                        <div className={styles.thumbnailWrapper} onClick={() => openPdf(previewUrl)}>
                                            <i className="fa-solid fa-file-pdf fa-3x" style={{ color: '#d9534f' }} />
                                        </div>
                                    ) : (
                                        <img className={styles.thumbnail} src={previewUrl} alt={`${key} preview`} />
                                    ))}

                                <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                            </div>
                        );
                    })}
                    <Button label={translations.save} mode={ButtonMode.primary} onClick={() => submitForm()} classes="w-100" />
                </>
            </Form>
        </Container>
    );
};
